import { useEffect } from 'react';

// Attaches the "wheel scrolls this box, never the page behind it" rule to an element: it stops the wheel from reaching the page's
// own scroll script, and from chaining to it at the top or end of the box. A box with nothing to scroll lets the page scroll as
// usual. The listener is native (not React's) so it runs before the page's handler.
export const containWheel = el => {
  if (!el) return undefined;
  const onWheel = e => {
    if (el.scrollHeight <= el.clientHeight) return;
    e.stopPropagation();
    const atTop = el.scrollTop <= 0 && e.deltaY < 0;
    const atBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 1 && e.deltaY > 0;
    if (atTop || atBottom) e.preventDefault();
  };
  el.addEventListener('wheel', onWheel, { passive: false });
  return () => el.removeEventListener('wheel', onWheel);
};

// For a box held in a ref.
export const useContainWheel = ref => {
  useEffect(() => containWheel(ref.current));
};

// The other way round: the box scrolls first, and once it is at its top (wheel up) or its end (wheel down) the same wheel scrolls the
// page on, so the page never has to be reached with the mouse outside the box. While the box can still scroll that way, the page
// does not hear the wheel. A box with nothing to scroll, and a sideways wheel, are left alone. The page is scrolled here, by hand:
// its own wheel script does not pick up a wheel that started inside the box.
const scrollParentOf = el => {
  for (let parent = el.parentElement; parent && parent !== document.body; parent = parent.parentElement) {
    if (
      /(auto|scroll|hidden)/.test(getComputedStyle(parent).overflowY) &&
      parent.scrollHeight > parent.clientHeight + 1
    ) {
      return parent;
    }
  }
  return null;
};

export const chainWheel = el => {
  if (!el) return undefined;
  const onWheel = e => {
    if (el.scrollHeight <= el.clientHeight || Math.abs(e.deltaX) > Math.abs(e.deltaY)) return;
    const atTop = el.scrollTop <= 0 && e.deltaY < 0;
    const atBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 1 && e.deltaY > 0;
    if (!atTop && !atBottom) {
      e.stopPropagation();
      return;
    }
    const page = scrollParentOf(el);
    if (!page) return;
    e.preventDefault();
    page.scrollTop += e.deltaMode === 1 ? e.deltaY * 16 : e.deltaY;
  };
  el.addEventListener('wheel', onWheel, { passive: false });
  return () => el.removeEventListener('wheel', onWheel);
};

export const useChainWheel = ref => {
  useEffect(() => chainWheel(ref.current));
};

export default useContainWheel;
